/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
'use client';

import { useCallback, useEffect, useMemo, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { getDefaultClassNames, type DateRange as DayPickerDateRange } from 'react-day-picker';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Switch } from '@/components/ui/switch';
import { getDayPickerLocale } from '@/lib/day-picker-locale';
import { cn } from '@/lib/utils';
import {
    formatDateFacetRefineValue,
    getThisWeekendRange,
    parseDateRangeFromRefines,
    type DateRange,
} from '@/components/eataly-plp-filters/lib/date-facet';

function startOfToday(): Date {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate());
}

function isSameDay(a: Date, b: Date): boolean {
    return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function isWeekendRange(range: DayPickerDateRange | undefined, today: Date): boolean {
    if (!range?.from || !range.to) {
        return false;
    }
    const weekend = getThisWeekendRange(today);
    return isSameDay(range.from, weekend.from) && isSameDay(range.to, weekend.to);
}

function toDayPickerRange(range: DateRange | null): DayPickerDateRange | undefined {
    if (!range) {
        return undefined;
    }
    return { from: range.from, to: range.to };
}

export type EatalyPlpDateFilterPanelProps = {
    refine: string[];
    onApply: (rangeString: string | null) => void;
    onClose: () => void;
    /** Reset draft when the panel opens. */
    open: boolean;
    className?: string;
};

export default function EatalyPlpDateFilterPanel({
    refine,
    onApply,
    onClose,
    open,
    className,
}: EatalyPlpDateFilterPanelProps): ReactElement {
    const { t, i18n } = useTranslation('categoryPlp');
    const locale = useMemo(() => getDayPickerLocale(i18n.language), [i18n.language]);
    const today = useMemo(() => startOfToday(), []);
    const defaultClassNames = getDefaultClassNames();

    const appliedRange = useMemo(() => parseDateRangeFromRefines(refine), [refine]);
    const [draftRange, setDraftRange] = useState<DayPickerDateRange | undefined>(() => toDayPickerRange(appliedRange));
    const [weekendOn, setWeekendOn] = useState(() => isWeekendRange(toDayPickerRange(appliedRange), today));

    useEffect(() => {
        if (!open) {
            return;
        }
        const next = toDayPickerRange(parseDateRangeFromRefines(refine));
        setDraftRange(next);
        setWeekendOn(isWeekendRange(next, today));
    }, [open, refine, today]);

    const handleSelect = useCallback(
        (range: DayPickerDateRange | undefined) => {
            setDraftRange(range);
            setWeekendOn(isWeekendRange(range, today));
        },
        [today]
    );

    const handleWeekendChange = useCallback(
        (checked: boolean) => {
            setWeekendOn(checked);
            if (checked) {
                const weekend = getThisWeekendRange(today);
                setDraftRange({ from: weekend.from, to: weekend.to });
            } else {
                setDraftRange(undefined);
            }
        },
        [today]
    );

    const handleReset = useCallback(() => {
        setDraftRange(undefined);
        setWeekendOn(false);
        onApply(null);
        onClose();
    }, [onApply, onClose]);

    const handleApply = useCallback(() => {
        if (!draftRange?.from) {
            onApply(null);
            onClose();
            return;
        }
        const to = draftRange.to ?? draftRange.from;
        onApply(formatDateFacetRefineValue(draftRange.from, to));
        onClose();
    }, [draftRange, onApply, onClose]);

    return (
        <div
            data-testid="plp-date-filter-panel"
            className={cn('relative flex min-h-0 w-full flex-1 flex-col', className)}>
            <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto overscroll-contain">
                <div className="flex items-center justify-between gap-2 border-b border-neutral-80 pb-6 pt-3">
                    <p className="body-copy-m-bold flex-1 text-neutral-10">{t('dateFilter.thisWeekend')}</p>
                    <Switch
                        checked={weekendOn}
                        onCheckedChange={handleWeekendChange}
                        aria-label={t('dateFilter.thisWeekend')}
                        data-testid="plp-date-filter-weekend-switch"
                    />
                </div>

                <Calendar
                    mode="range"
                    locale={locale}
                    weekStartsOn={1}
                    defaultMonth={today}
                    selected={draftRange}
                    onSelect={handleSelect}
                    disabled={{ before: today }}
                    showOutsideDays={false}
                    navLayout="around"
                    className="w-full p-0"
                    classNames={{
                        weekday: cn(
                            'label-m flex-1 py-1 text-center uppercase text-neutral-40',
                            defaultClassNames.weekday
                        ),
                        day: cn('relative flex-1 p-0 text-center', defaultClassNames.day),
                        day_button: cn(
                            'body-copy-m-bold mx-auto flex size-[42px] items-center justify-center rounded-full text-neutral-10 hover:bg-sand-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50',
                            defaultClassNames.day_button
                        ),
                        today: cn('[&>button]:border [&>button]:border-sand-90', defaultClassNames.today),
                        selected: cn(
                            '[&>button]:bg-primary [&>button]:text-primary-foreground [&>button]:hover:bg-primary',
                            defaultClassNames.selected
                        ),
                        range_start: cn(
                            '[&>button]:rounded-full [&>button]:bg-primary [&>button]:text-primary-foreground',
                            defaultClassNames.range_start
                        ),
                        range_end: cn(
                            '[&>button]:rounded-full [&>button]:bg-primary [&>button]:text-primary-foreground',
                            defaultClassNames.range_end
                        ),
                        range_middle: cn(
                            'rounded-none bg-brown-90 [&>button]:rounded-none [&>button]:bg-transparent [&>button]:text-neutral-10',
                            defaultClassNames.range_middle
                        ),
                    }}
                />
            </div>

            <div className="flex w-full shrink-0 gap-2 pt-6">
                <Button
                    type="button"
                    hierarchy="secondary"
                    mode="light"
                    size="large"
                    className="flex-1 cursor-pointer uppercase"
                    onClick={handleReset}
                    data-testid="plp-date-filter-reset">
                    {t('dateFilter.reset')}
                </Button>
                <Button
                    type="button"
                    hierarchy="primary"
                    mode="light"
                    size="large"
                    className="flex-1 cursor-pointer uppercase"
                    onClick={handleApply}
                    data-testid="plp-date-filter-apply">
                    {t('dateFilter.apply')}
                </Button>
            </div>
        </div>
    );
}
